import { Component, Input, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';

import { SupportApiService } from '../core/support-api.service';
import { TicketPriority } from '../core/models';

/** Spec — priority-override control. Requires a non-empty reason before the
 * `patchTicket` write can fire, so every agent-driven priority change on the
 * wire carries a `priority_override_reason` (ticket_transitions, Rec 5).
 *
 * `newPriority`/`reason` are signal-backed (same reason as
 * EscalateModalComponent's fields): a signal write notifies the template's
 * two-way `[(ngModel)]` binding synchronously even when set directly on the
 * component instance outside a zone-tracked event (as this component's spec
 * does), avoiding a false-positive NG0100
 * (ExpressionChangedAfterItHasBeenChecked) between two `detectChanges()`
 * calls. */
@Component({
  selector: 'app-priority-override-control',
  standalone: true,
  imports: [FormsModule, NgbTooltip],
  template: `
    <div class="override">
      <div class="bt-field">
        <label class="bt-label" ngbTooltip="Overriding replaces the derived priority and is recorded as an agent override">Override priority</label>
        <select class="bt-select" data-testid="override-priority-select" [(ngModel)]="newPriority">
          <option [ngValue]="undefined">Change priority…</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="low">Low</option>
          <option value="feature_request">Feature request</option>
        </select>
      </div>
      <input class="bt-input" data-testid="override-reason" [(ngModel)]="reason" placeholder="Reason (required)"
             ngbTooltip="Required — recorded alongside the override in the ticket's history" />
      <button type="button" class="bt-btn bt-btn--secondary bt-btn--sm override__submit"
              data-testid="override-submit" [disabled]="!canSubmit()" (click)="submit()"
              ngbTooltip="Override the derived priority and record the reason">
        Override priority
      </button>
    </div>
  `,
  styles: [`
    :host { display: block; border-top: 1px solid var(--bt-border-light); }
    .override { display: flex; flex-direction: column; gap: 0.6rem; }
    .override__submit { align-self: flex-start; }
  `],
})
export class PriorityOverrideControlComponent {
  private api = inject(SupportApiService);

  @Input() ticketId = '';
  @Input() currentPriority: TicketPriority | '' = '';

  private newPrioritySignal = signal<TicketPriority | undefined>(undefined);
  get newPriority(): TicketPriority | undefined {
    return this.newPrioritySignal();
  }
  set newPriority(value: TicketPriority | undefined) {
    this.newPrioritySignal.set(value);
  }

  private reasonSignal = signal('');
  get reason(): string {
    return this.reasonSignal();
  }
  set reason(value: string) {
    this.reasonSignal.set(value);
  }

  canSubmit(): boolean {
    return !!this.newPriority && this.reason.trim().length > 0;
  }

  submit(): void {
    if (!this.canSubmit() || !this.newPriority) return;
    this.api.patchTicket(this.ticketId, {
      priority_override: this.newPriority, priority_override_reason: this.reason,
    }).subscribe();
  }
}
